import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Info, Megaphone, TriangleAlert } from 'lucide-react';
import { useRelativeTime } from '@hooks/useRelativeTime';
import type { Announcement } from '../../types/dashboard';
import { cn } from '@/lib/utils';

const TYPE_STYLE = {
  success: { Icon: CheckCircle2, chip: 'bg-success/10 text-success' },
  warning: { Icon: TriangleAlert, chip: 'bg-warning/10 text-warning' },
  info: { Icon: Info, chip: 'bg-primary/10 text-primary' },
  general: { Icon: Megaphone, chip: 'bg-muted text-muted-foreground' },
} as const;

/** One announcement. Nothing here is clickable, so the row offers no hover affordance. */
const AnnouncementRow: React.FC<{ announcement: Announcement }> = ({ announcement }) => {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime();
  const { Icon, chip } = TYPE_STYLE[announcement.type] ?? TYPE_STYLE.general;

  return (
    <li className="flex gap-3 p-4">
      <span className={cn('grid size-8 shrink-0 place-items-center rounded-lg', chip)}>
        <Icon className="size-4" strokeWidth={2} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p className="revy-cjk min-w-0 flex-1 text-sm font-medium leading-[1.5]">
            {announcement.title}
          </p>
          {announcement.priority === 'high' && (
            <span className="mt-0.5 shrink-0 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-medium leading-[1.5] text-warning">
              {t('dashboard.priorityHigh')}
            </span>
          )}
        </div>

        <p className="revy-cjk mt-1 text-[13px] leading-[1.6] text-muted-foreground">
          {announcement.message}
        </p>

        <p className="revy-cjk mt-2 text-xs leading-[1.5] text-muted-foreground">
          {announcement.author}
          {relativeTime(announcement.createdAt) && ` · ${relativeTime(announcement.createdAt)}`}
        </p>
      </div>
    </li>
  );
};

export default AnnouncementRow;
